// Canonical definition of mechanism evidence types: their ordering (how well a
// mechanism is supported), the icon + tooltip + label shown for each, and small
// helpers to rank a value or pick the best-supported one from a list.
//
// This is the single source of truth for evidence. Everything that draws an
// evidence glyph (the reaction tables, the browse cards, the mechanism viewer's
// node badges, the advanced-search filters) should import from here so the
// vocabulary can't drift apart. The evidence note itself is expected to change
// in the future — when it does, update ONLY this file.
import {
  faClone,
  faCodeBranch,
  faCalculator,
  faFlask,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";

// Bucket for anything not (yet) a recognised evidence value: null, "TRUE",
// "nan", "missing_value", "", unknown strings, … They are all treated as
// "evidence missing in the database" and rank lowest.
export const EVIDENCE_MISSING = "missing";

// The four recognised evidence values, in the order used by the database.
const KNOWN_EVIDENCE = ["similarity", "trivial", "calculation", "experiment"];

// Evidence ordered from least to best supported. Index = rank (higher = better).
export const EVIDENCE_ORDER = [EVIDENCE_MISSING, ...KNOWN_EVIDENCE];

const EVIDENCE_META = {
  experiment: {
    icon: faFlask,
    label: "Experiment",
    tooltip: "Mechanism was discovered by conducting chemical experiments.",
  },
  calculation: {
    icon: faCalculator,
    label: "Calculation",
    tooltip: "Mechanism was described using calculation methods.",
  },
  trivial: {
    icon: faCodeBranch,
    label: "Trivial",
    tooltip: "Trivial from established route",
  },
  similarity: {
    icon: faClone,
    label: "Similarity",
    tooltip:
      "Mechanism was deduced using analogy and similarity to other mechanisms.",
  },
  [EVIDENCE_MISSING]: {
    icon: faQuestion,
    label: "Evidence missing",
    tooltip: "Evidence missing in the database.",
  },
};

// Map any raw evidence value to one of the canonical keys. Unknown / empty
// values collapse to EVIDENCE_MISSING.
export function normalizeEvidence(evidence) {
  if (typeof evidence === "string") {
    const value = evidence.trim().toLowerCase();
    if (KNOWN_EVIDENCE.includes(value)) return value;
  }
  return EVIDENCE_MISSING;
}

// Numeric rank for sorting/comparison (higher = better supported).
export function evidenceRank(evidence) {
  return EVIDENCE_ORDER.indexOf(normalizeEvidence(evidence));
}

// Best-supported evidence among a list of raw values. Empty list -> missing.
export function bestEvidence(evidenceList) {
  if (!evidenceList || evidenceList.length === 0) return EVIDENCE_MISSING;
  return evidenceList.reduce(
    (best, current) =>
      evidenceRank(current) > evidenceRank(best) ? normalizeEvidence(current) : best,
    EVIDENCE_MISSING,
  );
}

export function evidenceIcon(evidence) {
  return EVIDENCE_META[normalizeEvidence(evidence)].icon;
}

export function evidenceTooltip(evidence) {
  return EVIDENCE_META[normalizeEvidence(evidence)].tooltip;
}

export function evidenceLabel(evidence) {
  return EVIDENCE_META[normalizeEvidence(evidence)].label;
}
